<template lang='pug'>
  #app(
    data-allow-mismatch="class"
    :data-watcher-test="rogue.observeData.watcherTest"
    :data-theme="route.meta.darkMode ? 'dark' : 'light'"
    :class="[mq, \
    rogue.appContext && 'rogue-native', \
    chatOnline && 'chat-online', \
    highContrast && 'high-contrast', \
    isFunneled && 'funneled']"
  )
    rhpa-login-drawer(v-if="overlay === 'programLogin'")
    country-selection-overlay(v-if="overlay === 'country-select-v2'" @close="uiStore.hideOverlay()")
    a11y-links(:high-contrast="highContrast" @highContrast="highContrast = $event")
    zendesk-messenger(v-if="hasLiveChat")
    missed-deals-modal(v-if="cart.items.length > 0 && listStore.missedDeals.length > 0")
    cookie-agree(v-if="cookieConsentEnabled")
    christmasShipping(v-if="showChristmasShippingBanner")
    apoBanner(v-if="showApoBanner")
    rogue-header(v-if="!rogue.appContext && !isFunneled && !uiStore.hideSiteChrome")
    .mbn-banner-reserve(v-if="reserveMbnBanner")
      mbn-banner(:showForm="false")
    confirm-region(v-if="allowRegionSelectPopup")
    funneled(v-if="!rogue.appContext && isFunneled && !hideHeader")
    router-view.main-layout-content#main-content
    side-cart(v-if="overlay === 'side-cart'")
    five-for-five-drawer.five-for-five(v-if="showFiveForFiveDrawer")
    rogue-footer(v-if="!rogue.appContext && !uiStore.hideMbnBanner")
    picture-in-picture(v-if="uiStore.pictureInPicture && !route.meta.isAdminRoute")
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeMount, onMounted, provide, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useMQ } from 'sisu/js/mq'
import type { PictureInPicture } from './stores/uiStoreTypes'
import { useConfigStore } from '@/js/stores/configStore'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useListStore } from '@/js/stores/listStore'
import { usePromoStore } from '@/js/stores/promoStore'
import { useUIStore } from '@/js/stores/uiStore'
import { loadScript } from '@/js/helpers/scripts'
import metadata, { type MetadataType } from '@/js/helpers/metadata'
import { canSkipRefresh } from '@/js/app-bridge/helpers'
import { useLiveChat } from '@/js/composables/useLiveChat'
import rogueHeader from '@/js/components/header/rogue-header.vue'
import funneled from '@/js/components/header/funneled.vue'
import rogueFooter from '@/js/components/footer/index.vue'
import A11yLinks from '@/js/components/rogue-a11y.vue'
import { initClickTracking } from '@/js/helpers/tracking'
import { getCookie, setCookie } from '@/js/services/Utils'
import ConfirmRegion from '@/js/components/banners/confirm-region.vue'
import { useRogue } from '@/js/services/Rogue'

const ZendeskMessenger = defineAsyncComponent(() => import(/* webpackChunkName: "zendesk-messenger" */ 'Src/js/components/header/zendesk-messenger.vue'))
const missedDealsModal = defineAsyncComponent(() => import(/* webpackChunkName: "missed-deals" */ 'Src/js/pages/checkout/components/missed-deals-modal.vue'))
const sideCart = defineAsyncComponent(() => import(/* webpackChunkName: "sidecart" */ 'Src/js/components/side-cart/index.vue'))
const cookieAgree = defineAsyncComponent(() => import(/* webpackChunkName: "cookieAgree" */ 'Src/js/components/cookies/cookie-agree.vue'))
const fiveForFiveDrawer = defineAsyncComponent(() => import(/* webpackChunkName: "five-for-five-drawer" */ 'Src/js/components/five-for-five-drawer.vue'))
const christmasShipping = defineAsyncComponent(() => import(/* webpackChunkName: "christmasShipping" */ 'Src/js/components/banners/christmas-shipping-deadline-banner.vue'))
const apoBanner = defineAsyncComponent(() => import(/* webpackChunkName: "apoBanner" */ 'Src/js/components/banners/apo-banner.vue'))
const CountrySelectionOverlay = defineAsyncComponent(() => import(/* webpackChunkName: "country-select" */'@/js/components/header/country-select/country-selection-overlay.vue'))
// camelCase so the binding does not collide with the PictureInPicture type imported above
const pictureInPicture = defineAsyncComponent(() => import(/* webpackChunkName: "picture-in-picture" */'@/js/components/events/invitational/picture-in-picture.vue'))
const RhpaLoginDrawer = defineAsyncComponent(() => import('@/js/components/rhpa/programs/rhpa-login-drawer.vue'))

const REGION_SELECT_SITES = ['EUR', 'GBR', 'USA']
const REGION_SELECT_DISABLED_PATHS = ['/tickets']
const APP_PAGE_TYPES = ['product', 'category']
const CHALLENGES_NATIVE_SEPARATION_PATHS = [
  '/challenges/about',
  '/challenges/challenge-schedule',
  '/challenges/leaderboard',
  '/challenges/faqs',
  '/rg-app/challenges/about',
  '/rg-app/challenges/challenge-schedule',
  '/rg-app/challenges/leaderboard',
  '/rg-app/challenges/faqs',
]

const rogue = useRogue()
const route = useRoute()
const router = useRouter()
const mq = useMQ()
const { hasLiveChat, chatOnline } = useLiveChat()

const configStore = useConfigStore()
const customerStore = useCustomerStore()
const listStore = useListStore()
const promoStore = usePromoStore()
const uiStore = useUIStore()

const highContrast = ref(false)
let healthIntervals: ReturnType<typeof setInterval> | undefined

const isFunneled = computed(() => route.meta.isFunneled)
const hideHeader = computed(() => route.meta.hideHeader)
const overlay = computed(() => uiStore.overlay)
const cart = computed(() => listStore.cart)
const activePromos = computed(() => promoStore.activePromos)

const allowRegionSelectPopup = computed(() => !rogue.appContext
  && REGION_SELECT_SITES.includes(rogue.site.code)
  && !REGION_SELECT_DISABLED_PATHS.some(p => rogue.initialRoute.includes(p)))


const showMbnBanner = computed(() => rogue.featureFlags.isOn('matte_black_enabled')
  && !route.path.includes('/deals') && route.path !== '/' && !isFunneled.value
  && uiStore.overlay !== 'CompetitionMobileMenu'
  && !uiStore.hideMbnBanner)

const showFiveForFiveDrawer = computed(() => rogue.featureFlags.isOn('fiveforfive_enabled') && !uiStore.pictureInPicture && !uiStore.hideMbnBanner
  && !uiStore.hideFiveForFiveDrawer)

const showPictureInPicture = computed(() => {
  // Growthbook will only return a value if this feature flag is enabled. No value means disabled
  const pip: PictureInPicture = rogue.featureFlags.eval('pip') as PictureInPicture

  return !rogue.appContext
    && pip?.type === 'YouTube'
    && (!pip?.nomq || pip.nomq.indexOf(mq.value) === -1)
    && !getCookie('hide-pip')
    && !route.path.includes('/live')
    && !route.path.includes('/invitational')
})

function applyPictureInPicture() {
  const pip: PictureInPicture = rogue.featureFlags.eval('pip') as PictureInPicture

  // Copied rather than muted in place: the flag value is Growthbook's own object
  uiStore.showPictureInPicture(showPictureInPicture.value ? { ...pip, controls: { ...pip.controls, volume: 0 } } : undefined)
}

const showChristmasShippingBanner = computed(() => rogue.featureFlags.eval('show_christmas_shipping_banner') && !route.meta.hideChristmasShippingBanner)

const showApoBanner = computed(() => rogue.site.code === 'APO' && !rogue.appContext && uiStore.overlay !== 'CompetitionMobileMenu')

const additionalAppPageFilter = computed(() => !rogue.appContext
  || (route.meta.type && rogue.appContext && (APP_PAGE_TYPES.includes(route.meta.type as string)
    || route.path.startsWith('/deals'))))

/**
 * MbnBanner is an async component, so its markup — and the min-height it reserves for itself —
 * only exist once its chunk resolves. It renders above the router-view, so mounting late pushes
 * the whole page down.
 */
const reserveMbnBanner = computed(() => showMbnBanner.value
  && additionalAppPageFilter.value
  && !route.meta.removeBanners)

const cookieConsentEnabled = computed(() => !route.meta.removeCookieAgree && !route.meta.isAdminRoute)

// Detect if on a specific challenge page to enable native/mobile device visibility separation
// Only these specific pages use the new behavior where "native" and "mobile" checkboxes are separate
const isChallengesNativeSeparationPage = computed(() => CHALLENGES_NATIVE_SEPARATION_PATHS.includes(route.path))

// Provide makes specific fields available to SISU components that can't access Pinia stores
provide('primarySegment', computed(() => customerStore.primarySegment || 'None'))
provide('loggedIn', computed(() => customerStore.loggedIn || false))
provide('loadScript', (s: string) => loadScript(s, rogue))
provide('overlay', overlay)
provide('showOverlay', (o: string) => uiStore.showOverlay(o, rogue))
provide('hideOverlay', uiStore.hideOverlay)
provide('drawerActive', uiStore.drawerActive)
provide('pictureInPicture', computed(() => uiStore.pictureInPicture))
provide('showPictureInPicture', uiStore.showPictureInPicture)
provide('hideMbnBanner', (hide: boolean) => { uiStore.hideMbnBanner = hide })
provide('hideSiteChrome', (hide: boolean) => { uiStore.hideSiteChrome = hide })
provide('activePromos', activePromos)
provide('hundoProductIds', computed(() => promoStore.hundoProductIds))
// Called by sisu's YouTubeVideo on each player's first play; only competition pages track it
provide('onVideoPlay', (videoName?: string) => {
  const path = route.path
  if (!path.includes('arnold-strongman-classic') && !path.includes('invitational')) {
    return
  }

  rogue.track.track({
    type  : 'custom',
    event : path.includes('arnold') ? 'Arnold Strongman Video Play' : 'Invitational Video Play',
    data  : { videoName },
  })
})
provide('refreshPromos', async () => { await promoStore.refresh(rogue) })

let siteName = rogue.site.name || 'Rogue Fitness'

let country = ''

if (['EUR', 'GBR'].includes(rogue.site.code) && rogue.l10n.path && rogue.l10n.country) {
  country = rogue.l10n.country.toUpperCase()
  if (country === 'GB') {
    country = 'UK'
  }
  if (country) {
    siteName = siteName.replace('Europe', country)
  }
}

const headData: MetadataType = {
  titleTemplate: (chunk: string | undefined): string | null => {
    if (route.name === 'Home') {
      if (country) {
        return chunk?.replace('Europe', country) || null
      }

      return chunk || null
    }

    return `${chunk} | ${siteName || 'Rogue Fitness'}`
  },
  link        : [],
  title       : `${siteName} - ${rogue.__('Strength & Conditioning Equipment')}`,
  locale      : rogue.l10n.locale,
  description : rogue.__('Rogue Fitness is the leading manufacturer in strength and conditioning equipment & an official sponsor of the CrossFit® Games, Arnold Classic, and USA Weightlifting. From power racks, rigs, and barbells to shoes, apparel & accessories, our online store equips garage gyms, military, pros & more.'),
  image       : 'https://assets.roguefitness.com/image/upload/v1610106897/media/rogue-og.jpg',
  type        : 'product',
  meta        : [
    { property: 'fb:app_id', content: '1664713057151785' },
    { property: 'fb:admins', content: '731170917' },
  ],
}

if (rogue.site.code === 'USA') {
  headData.link!.push({
    rel   : 'search',
    href  : `${rogue.baseURL}/search.xml`,
    type  : 'application/opensearchdescription+xml',
    title : siteName,
  })
}

useHead(metadata(headData, rogue))

function startHealthIntervals() {
  rogue.log.debug(`Adding health interval ${new Date().toISOString()}`)
  let min = 0
  clearInterval(healthIntervals)

  healthIntervals = setInterval(() => {
    rogue.log.debug(`Running health interval ${new Date().toISOString()}`)
    // Reload promos every minute
    if (min % 2 === 0) {
      promoStore.refresh(rogue)
    }

    if (canSkipRefresh(rogue)) {
      // Skip refresh process for the app: in webviews that are used as tabs / if the app is in background
      return
    }

    if (min % 5 === 0) {
      // Reload app config every 5 minutes
      customerStore.loadAppConfig(rogue)
    }
    // Reload heartbeat every 2 minutes
    if (min % 2 === 0) {
      rogue.checkHeartbeat()
    }

    min += 1
  }, 60 * 1000)
}

// Update HTML element class when navigating to/from specific challenge pages
watch(isChallengesNativeSeparationPage, (isNativeSeparationPage: boolean) => {
  if (typeof document === 'undefined') {
    return
  }
  const htmlEl = document.documentElement
  if (isNativeSeparationPage) {
    htmlEl.classList.add('challenges-native-separation')
  } else {
    htmlEl.classList.remove('challenges-native-separation')
  }
}, { immediate: true })

configStore.init(rogue)

if (route.query?.highcontrast === '1') {
  setCookie('highcontrast', '1', 365)
  highContrast.value = true
}

onBeforeMount(() => {
  Promise.all([
    customerStore.init(rogue),
    listStore.init(rogue),
  ]).then(async () => {
    promoStore.reloadCustomerPricing(rogue)
  })
})

onMounted(() => {
  const constructorConfig = rogue.keys.constructor
  if (constructorConfig) {
    const lang = rogue.l10n.lang
    const indexKey = constructorConfig.languageKeys?.[lang] ?? constructorConfig.indexKey
    const w = window as any
    w.cnstrc = w.cnstrc || {}
    w.cnstrc.indexKey = indexKey

    rogue.featureFlags.ensureConstructorTestCell()
  }

  // Delay these, since they arent needed right at page load
  setTimeout(() => {
    promoStore.refresh(rogue)

    if (canSkipRefresh(rogue)) {
      // Skip refresh process for the app: in webviews that are used as tabs / if the app is in background
      return
    }
    configStore.loadCountries(rogue)

    // The page's flags are as old as its cached HTML. Fetch the live ones now instead of
    // waiting for the first health interval. Flags only, so the service worker doesn't
    // start precaching a new release during page load.
    rogue.checkHeartbeat(false)

    applyPictureInPicture()

    // Re-applied when a heartbeat changes the pip flag, so a new video reaches cached pages
    // without a Cloudflare purge. Compared by value because every new payload yields a new
    // object, and re-applying an unchanged pip would restart the video.
    watch(() => JSON.stringify(rogue.featureFlags.eval('pip')), applyPictureInPicture)
  }, 1000)

  if (route.query.rgsmigrate) {
    const query = Object.assign({}, route.query)
    delete query.rgsmigrate
    router.replace({ query })
  }

  configStore.checkApplePay(rogue)
  configStore.checkGooglePay(rogue)

  startHealthIntervals()

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      rogue.log.debug('Background - Disabling health interval ')
      clearInterval(healthIntervals)
    } else {
      startHealthIntervals()
      rogue.checkHeartbeat()
    }
  })

  initClickTracking(rogue)
})
</script>

<style lang="scss">
@use "./../scss/contrast.scss";

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;

  .main-layout-content {
    flex-grow: 1;
  }

  &[data-theme='dark'] .main-layout-content {
    background-color: $color-black;
  }
}

.icon-flex {
  display: flex;
  align-items: center;

  .icon:first-child {
    margin-right: $space-xxs;
  }

  .icon:last-child {
    justify-self: flex-end;
  }

  &.center {
    justify-content: center;
  }
}

.max-wrapper {
  margin: 0 auto;
  padding: $space-m;
  width: $max-width;
  max-width: 100%;
  min-height: 500px;
}

.grecaptcha-badge {
  visibility: hidden;
}

.pca .pcaautocomplete .pcafooter {
  display: none;
}

.mbn-banner-reserve {
  min-height: $size-xxxxxxl;
}

@include mobile() {
  .five-for-five+footer {
    margin-bottom: 64px;
  }
}
</style>

<style lang="scss" target="app">
@use 'Src/scss/variables.scss' as *;

:root {
  --above-header-height: 0px;
  --checkout-banner-height: 0px;
  --cookie-agree-height: 0px;
  --mbn-banner-height: 0px;
  --header-height: 0px;
}

// Required to make the page scrollable inside iFrame for iOS
// Not needed in React Native app
.platform-ios:not(.rogue-app-rn) {

  &,
  body {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  &:not(.checkout) {

    &,
    body {
      @include scrollable;
    }
  }
}

.platform-ios.rogue-app-rn:not(.no-scroll) {

  &,
  body {
    overflow-y: scroll;
    scroll-behavior: smooth;
  }
}
</style>
